import {} from 'google.maps';
import { Directive, NgZone, ElementRef, OnInit, HostListener, Output, EventEmitter } from '@angular/core';
import * as $ from 'jquery';
import { MapsAPILoader } from '@agm/core';
import { Observable } from 'rxjs/Observable';
import { ActivatedRoute } from '@angular/router';

@Directive({
    selector: '[appAddressAutocomplete]',
})
export class AddressAutocompleteDirective implements OnInit {
    @Output() inProgress: EventEmitter<any> = new EventEmitter();
    autoComplete: google.maps.places.Autocomplete;
    addrComponent: any;
    oldAddress: string;
    urlParams: any = null;
    placeChangeInProgress = false;

    get isDemo (): boolean {
        return this.urlParams && this.urlParams.demo !== undefined;
    }

    constructor(
        private el: ElementRef,
        private mapsLoader: MapsAPILoader,
        private ngZone: NgZone,
        private route: ActivatedRoute
    ) {
        this.route.queryParams.subscribe(
            params => this.urlParams = params
        );
    }

    ngOnInit() {
        this.el.nativeElement.setAttribute('data-address-autocomplete', false);
        this.initAutoComplete();
    }

    initAutoComplete() {
        this.mapsLoader.load().then(() => {
            this.autoComplete = new google.maps.places.Autocomplete(this.el.nativeElement, { types: ['geocode'], fields: ['formatted_address', 'address_components'] });
            this.autoComplete.setComponentRestrictions({'country': ['us', 'pr', 'vi', 'gu', 'mp']});

            this.autoComplete.addListener('place_changed', this.onPlaceChanged.bind(this));
        });
    }

    onPlaceChanged() {
        this.ngZone.run(() => {
            this.placeChangeInProgress = true;
            const place: google.maps.places.PlaceResult = this.autoComplete.getPlace();
            if (!place.geometry || !place.address_components) {
                return;
            }

            const _addressComponents = this.makeAddressComponent(place.address_components);

            if (this.isGeoAddressValid(_addressComponents)) {
                this.setElementAttribute(_addressComponents);
                this.clearErrorMessage();
            } else {
                this.showErrorMessage();
            }
            this.placeChangeInProgress = false;
        });
    }

    checkReverseGeoCode(address) {
        if (address.length > 10) {
            this.geocoder(address).subscribe(({results, status}) => {
                if (results[0]) {
                    const addressComponent = this.makeAddressComponent(results[0].address_components);
                    if (this.isGeoAddressValid(addressComponent)) {
                        this.setElementAttribute(addressComponent);
                        this.el.nativeElement.value = results[0].formatted_address;
                        this.oldAddress = results[0].formatted_address;
                        this.clearErrorMessage();
                    } else {
                        this.showErrorMessage();
                    }
                } else {
                    this.clearErrorMessage();
                }
            });
        }
    }

    geocoder (address): Observable<{results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus}> {
        return new Observable(observer => {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address, componentRestrictions: {country: 'US'}, }, (results, status) => {
                if (results[0]) {
                    const addressComponent = this.makeAddressComponent(results[0].address_components);
                    if (!this.isGeoAddressValid(addressComponent) && this.isDemo) {
                        // Create payload base on input and prevent google validation //
                        // Format - xxxx route, locality, administrative_area_level_1, country//
                        // [street_number] - xxxx
                        // [postal_code] - 90010
                        // [route] - Wilshire Blvd
                        // [locality, political] - Los Angeles
                        // [administrative_area_level_1, political] - CA
                        // [political, country] - US
                        let formatted_address = address;
                        const addresses = address.split(',');
                        const observables: Observable<any>[] = [];
                        const address_components: google.maps.GeocoderAddressComponent[] = [];
                        if (addresses && addresses.length) {
                            const street_address = /([0-9]+)(.*)/g.exec(addresses[0]);
                            if (street_address.length) {
                                // Street or Unit # //
                                if (street_address.length > 1) {
                                    address_components.push({
                                        long_name: street_address[1],
                                        short_name: street_address[1],
                                        types: ['street_number']
                                    });
                                }
                                // Street or Suite //
                                if (street_address.length > 2) {
                                    street_address[2] = street_address[2].trim();
                                    address_components.push({
                                        long_name: street_address[2],
                                        short_name: street_address[2],
                                        types: ['route']
                                    });
                                }
                            }
                            // City //
                            if (addresses.length > 1) {
                                addresses[1] = addresses[1].trim();
                                address_components.push({
                                    long_name: addresses[1],
                                    short_name: addresses[1],
                                    types: ['locality', 'political']
                                });
                            }
                            // State //
                            if (addresses.length > 2) {
                                addresses[2] = addresses[2].trim();
                                const state = {
                                    long_name: addresses[2],
                                    short_name: addresses[2],
                                    types: ['administrative_area_level_1', 'political']
                                };
                                const state_postal_address = addresses[2].split(' ');
                                if (state_postal_address.length > 1) {
                                    state.long_name = state_postal_address[0];
                                    state.short_name = state_postal_address[0];
                                    address_components.push({
                                        long_name: state_postal_address[1],
                                        short_name: state_postal_address[1],
                                        types: ['postal_code']
                                    });
                                }
                                address_components.push(state);
                            }
                            // Country //
                            if (addresses.length > 3) {
                                addresses[3] = addresses[3].trim();
                                address_components.push({
                                    long_name: addresses[3],
                                    short_name: addresses[3],
                                    types: ['country', 'political']
                                });
                            }
        
                            formatted_address = addresses.join(', ');
                        }
        
                        const results = {results: [{
                            place_id: null,
                            geometry: null,
                            formatted_address,
                            address_components,
                            partial_match: null,
                            postcode_localities: null,
                            types: ['street_address'],
                        }], status: google.maps.GeocoderStatus.OK};
        
                        if (observables.length) {
                            Observable.forkJoin(observables)
                                .subscribe(response => {
                                    observer.next(results);
                                    observer.complete();
                                });
                            return;
                        }
                        observer.next(results);
                        observer.complete();
                        return;
                    }
                }
                observer.next({results, status});
                observer.complete();
            });
        });
    }

    showErrorMessage() {
        this.clearErrorMessage();
        $(this.el.nativeElement)
            .parent()
            .append('<span class="text-danger">Enter complete address (Street, City, State, Zip).</span>');
    }

    clearErrorMessage() {
        $(this.el.nativeElement)
            .parent()
            .find('span.text-danger')
            .remove();
    }

    makeAddressComponent(addressComponents): Array<any> {
        const _addressComponents: Array<any> = [];
        this.addrComponent = _addressComponents;
        addressComponents.forEach(comp => {
            _addressComponents[comp.types[0]] = comp.short_name;
        });
        if (!_addressComponents['locality'] && _addressComponents['sublocality_level_1']) {
            _addressComponents['locality'] = _addressComponents['sublocality_level_1'];
        }
        return _addressComponents;
    }

    isGeoAddressValid(addressComponents) {
        return addressComponents['street_number'] &&
            addressComponents['route'] &&
            addressComponents['locality'] &&
            addressComponents['administrative_area_level_1'] &&
            addressComponents['postal_code'];
    }

    setElementAttribute(addressComponents) {

        this.el.nativeElement.setAttribute('data-address-autocomplete', true);

        this.el.nativeElement.setAttribute(
            'data-street-number',
            addressComponents['street_number'] ? addressComponents['street_number'] : ''
        );
        if (addressComponents['subpremise']) {
            addressComponents['route'] = String(addressComponents['route']) + ' #' + String(addressComponents['subpremise']);
        }
        this.el.nativeElement.setAttribute(
            'data-street-name',
            addressComponents['route'] ? addressComponents['route'] : ''
        );

        this.el.nativeElement.setAttribute(
            'data-city',
            addressComponents['locality'] ? addressComponents['locality'] : ''
        );

        this.el.nativeElement.setAttribute(
            'data-state',
            addressComponents['administrative_area_level_1'] ? addressComponents['administrative_area_level_1'] : ''
        );

        this.el.nativeElement.setAttribute(
            'data-country',
            addressComponents['country'] ? addressComponents['country'] : ''
        );

        this.el.nativeElement.setAttribute(
            'data-zip-code',
            addressComponents['postal_code'] ? addressComponents['postal_code'] : ''
        );
    }

    resetElementAttribute() {
        this.el.nativeElement.setAttribute('data-address-autocomplete', false);
        this.el.nativeElement.removeAttribute('data-street-number');
        this.el.nativeElement.removeAttribute('data-street-name');
        this.el.nativeElement.removeAttribute('data-city');
        this.el.nativeElement.removeAttribute('data-state');
        this.el.nativeElement.removeAttribute('data-country');
        this.el.nativeElement.removeAttribute('data-zip-code');
    }

    @HostListener('paste', ['$event']) onpaste($event) {
        this.oldAddress = '';
        const address = $event.clipboardData.getData('Text');
        this.resetElementAttribute();
        this.checkReverseGeoCode(address);
    }

    @HostListener('keydown', ['$event']) onkeydown($event) {
        if ($event.key === 'Enter') {
            this.oldAddress = '';
            $event.preventDefault();
        } else if ($event.key !== 'Tab') {
            this.resetElementAttribute();
        } else {
            this.checkReverseGeoCode(this.el.nativeElement.value);
        }
    }

    @HostListener('change', ['$event']) onchange($event) {
        setTimeout(() => {
            this.checkReverseGeoCode(this.el.nativeElement.value);
        }, 300);
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/directives/address-autocomplete/address-autocomplete.directive.ts